import { Box, Grid, Stack, useTheme, Button, Typography } from '@mui/material';
import { useScreenSize } from '@/hooks/use-screen-size';
import { ArrowRightOutlined, EditOutlined, People } from '@mui/icons-material';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { SessionDetails } from '@/components/events/detail/tabs/sessions/session-details.tsx';
import UpdateSessionForm from '@/components/events/detail/tabs/sessions/sessions-grid/update-session.form.tsx';
import { ListDetailView } from '@/components/common/list-detail-view';
import { SessionActors } from '../session-actors/session-contributor-widget';
import { SessionPlaylistMediaWidget } from '@/components/sessions/session-playlist-media-widget';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import { SessionFlowsWidget } from './session-flows.widget';
import sessionTypeToIcon from '../helpers/session-type-to-icon.helper';
import { useTranslations } from '@/i18n/use-translations';
import { getObjectId } from '@/helpers/object.id.helper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SessionPublish, SessionDelete } from './session-actions';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import type { Mode } from '@/types/entity-mode';
import Widget from '@/components/common/widget';
import { pickNewerSession } from '@/components/flows/detail/flow-detail.utils';

const SessionDetailContent = ({ session, isWideScreen }: { session: SessionDto; isWideScreen: boolean }) => {
  const t = useTranslations();
  const sessionOriginId = session.origin ? getObjectId(session.origin) : session.id;
  const isPlanSession = session.mode === 'PLAN';
  const isOrganizeSession = session.mode === 'ORGANIZE' || session.mode === 'PERFORM';

  // Always fetch both modes — list/store copies often keep stale playlist embeds after upload
  // when an approved version already exists (version-based merge preferred the older assets[]).
  const { data: planSessionFromBackend } = useGetSessionQuery(sessionOriginId, 'PLAN');
  const { data: organizeSessionFromBackend } = useGetSessionQuery(sessionOriginId, 'ORGANIZE');

  const planSession =
    pickNewerSession(isPlanSession ? session : undefined, planSessionFromBackend) ??
    (isPlanSession ? session : planSessionFromBackend);
  const organizeSession =
    pickNewerSession(isOrganizeSession ? session : undefined, organizeSessionFromBackend) ??
    (isOrganizeSession ? session : organizeSessionFromBackend);

  const computedMode = useMemo(() => {
    if (isPlanSession) return 'PLAN';
    if (isOrganizeSession) return 'ORGANIZE';
    if (!planSession && organizeSession) return 'ORGANIZE';
    if (!organizeSession && planSession) return 'PLAN';
    return session.mode;
  }, [isPlanSession, isOrganizeSession, planSession, organizeSession, session.mode]);

  const [currentMode, setCurrentMode] = useState<Mode>(computedMode);
  const previousComputedModeRef = useRef<Mode>(computedMode);
  const userInitiatedChangeRef = useRef(false);

  // Only sync computedMode to currentMode when underlying data changes, not when user manually changes mode
  useEffect(() => {
    // If user manually changed mode, don't override it unless the underlying data changed
    if (userInitiatedChangeRef.current && computedMode === previousComputedModeRef.current) {
      return;
    }

    // Reset user-initiated flag if computed mode changed from external data
    if (computedMode !== previousComputedModeRef.current) {
      userInitiatedChangeRef.current = false;
      queueMicrotask(() => setCurrentMode(computedMode));
    }

    previousComputedModeRef.current = computedMode;
  }, [computedMode]);

  const handleModeChange = (newMode: Mode) => {
    if (newMode === 'PLAN' && !planSession) return;
    if (newMode === 'ORGANIZE' && !organizeSession) return;
    userInitiatedChangeRef.current = true;
    setCurrentMode(newMode);
  };

  const hasPlanMode = !!planSession;
  const hasOrganizeMode = !!organizeSession;
  const sessionToDisplay = currentMode === 'PLAN' ? (planSession ?? session) : (organizeSession ?? session);
  const sessionForPublish = planSession ?? (session.mode === 'PLAN' ? session : undefined);

  return (
    <Grid container spacing={2} sx={{ mt: isWideScreen ? 0 : 1 }}>
      <Grid container direction="column" size={{ xs: 12, lg: 6 }} spacing={2}>
        <Widget title={t('flows.mode', { defaultValue: 'BRITESTAGE Process' })} sx={{ borderRadius: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={0}
            sx={{
              height: '32px',
              minHeight: '32px',
              maxHeight: '32px',
              p: 0,
              width: '100%',
            }}
          >
            <Button
              variant={currentMode === 'PLAN' ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              onClick={() => handleModeChange('PLAN')}
              disabled={!hasPlanMode}
              sx={{ display: 'flex', gap: 0.5 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                1
              </Typography>
              <EditOutlined fontSize="small" />
            </Button>
            <ArrowRightOutlined fontSize="small" color="action" />
            {sessionForPublish ? <SessionPublish session={sessionForPublish} /> : null}
            <ArrowRightOutlined fontSize="small" color="action" />
            <Button
              variant={currentMode === 'ORGANIZE' ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              onClick={() => handleModeChange('ORGANIZE')}
              disabled={!hasOrganizeMode}
              sx={{ display: 'flex', gap: 0.5 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                2
              </Typography>
              <People fontSize="small" />
            </Button>
          </Stack>
        </Widget>
        <UpdateSessionForm
          session={sessionToDisplay}
          sessionPlan={planSession}
          sessionOrganize={organizeSession}
          alternativeWidgetButton={<Stack direction="row" spacing={0.5} />}
          modeOverride={currentMode}
        />
        <SessionFlowsWidget session={sessionToDisplay} context="library" />
      </Grid>
      <Grid container direction="column" size={{ xs: 12, lg: 6 }} spacing={2}>
        <SessionActors session={sessionToDisplay} />
        <SessionPlaylistMediaWidget session={sessionToDisplay} context="Session" enablePlayerSocket={false} />
      </Grid>
    </Grid>
  );
};

export const SessionsGrid = ({ sessions }: { sessions: SessionDto[] }) => {
  const theme = useTheme();
  const { isWideScreen } = useScreenSize({ breakpoint: 860 });
  const t = useTranslations();
  const { setSelectedSession } = useEventStore();
  const mode = useEventMode();

  const handleItemSelect = useCallback(
    (session: SessionDto | null) => {
      setSelectedSession(session || undefined);
    },
    [setSelectedSession],
  );

  const renderSessionListItem = (session: SessionDto, isSelected: boolean) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        'p': 1,
        'border': 1,
        'borderColor': isSelected ? 'primary.main' : 'divider',
        'borderRadius': 2,
        'boxShadow': 1,
        'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        'backgroundColor': isSelected
          ? 'action.selected'
          : theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
        'mb': 0.5,
        '&:hover': {
          boxShadow: 2,
          backgroundColor: isSelected ? 'action.selected' : 'action.hover',
        },
      }}
    >
      {sessionTypeToIcon(session.type)}
      <Box sx={{ flex: 1 }}>
        <SessionDetails session={session} />
      </Box>
      <Stack direction="row" spacing={0} sx={{ ml: 'auto' }}>
        {!isOrganizeMode(mode) && <SessionPublish session={session} />}
        <SessionDelete session={session} />
      </Stack>
    </Stack>
  );

  const renderSessionDetail = (session: SessionDto) => (
    <SessionDetailContent session={session} isWideScreen={isWideScreen} />
  );

  return (
    <ListDetailView
      items={sessions}
      renderListItem={renderSessionListItem}
      renderDetail={renderSessionDetail}
      getItemKey={(session) => session.id}
      getItemTitle={(session) => session.title}
      itemSubtitle={t('session.subtitle')}
      listGridSize={{ xs: 12, md: 6, lg: 4 }}
      detailGridSize={{ xs: 12, md: 6, lg: 8 }}
      onItemSelect={handleItemSelect}
    />
  );
};
